import React, { createContext, useContext, useState, useCallback } from "react";

// Supported languages
export const languages = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "zh", name: "中文", flag: "🇨🇳" },
  { code: "ar", name: "العربية", flag: "🇸🇦", rtl: true },
  { code: "ja", name: "日本語", flag: "🇯🇵" },
  { code: "ko", name: "한국어", flag: "🇰🇷" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
];

const TranslateContext = createContext();

// Cache translations to avoid redundant API calls
const translationCache = new Map();

export const TranslateProvider = ({ children, apiUrl }) => {
  const [currentLang, setCurrentLang] = useState(() => {
    return localStorage.getItem("preferredLanguage") || "en";
  });
  const [isTranslating, setIsTranslating] = useState(false);

  // API URL for translation proxy
  const TRANSLATE_API_URL = apiUrl || process.env.REACT_APP_TRANSLATE_API_URL || "https://api.ramirez-carlos.com";

  // Generate cache key
  const getCacheKey = (text, targetLang) => `${targetLang}:${text}`;

  // Translate text using Laravel backend proxy
  const translateText = useCallback(async (text, targetLang) => {
    if (!text || targetLang === "en") return text;

    const cacheKey = getCacheKey(text, targetLang);
    
    // Check cache first
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    try {
      const response = await fetch(`${TRANSLATE_API_URL}/translate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          text,
          targetLang,
          sourceLang: "en",
        }),
      });

      if (!response.ok) {
        throw new Error(`Translation failed: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.translations) {
        const translated = data.translations;
        translationCache.set(cacheKey, translated);
        return translated;
      }
      
      return text;
    } catch (error) {
      console.error("Translation error:", error);
      return text;
    }
  }, [TRANSLATE_API_URL]);

  // Translate multiple texts at once (batch)
  const translateBatch = useCallback(async (texts, targetLang) => {
    if (targetLang === "en") {
      return texts.reduce((acc, text, i) => ({ ...acc, [i]: text }), {});
    }

    const results = {};
    const textsToTranslate = [];
    const indices = [];

    // Check cache and identify what needs translation
    texts.forEach((text, index) => {
      const cacheKey = getCacheKey(text, targetLang);
      if (translationCache.has(cacheKey)) {
        results[index] = translationCache.get(cacheKey);
      } else {
        textsToTranslate.push(text);
        indices.push(index);
      }
    });

    if (textsToTranslate.length === 0) {
      return results;
    }

    try {
      const response = await fetch(`${TRANSLATE_API_URL}/translate/batch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
        },
        body: JSON.stringify({
          texts: textsToTranslate,
          targetLang,
          sourceLang: "en",
        }),
      });

      if (!response.ok) {
        throw new Error(`Batch translation failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.translations) {
        data.translations.forEach((translatedText, i) => {
          const originalIndex = indices[i];
          const originalText = textsToTranslate[i];
          
          results[originalIndex] = translatedText;
          translationCache.set(getCacheKey(originalText, targetLang), translatedText);
        });
      }
    } catch (error) {
      console.error("Batch translation error:", error);
      // Return original texts on error
      indices.forEach((index, i) => {
        results[index] = textsToTranslate[i];
      });
    }

    return results;
  }, [TRANSLATE_API_URL]);

  // Change language
  const changeLanguage = useCallback(async (langCode) => {
    setIsTranslating(true);
    setCurrentLang(langCode);
    localStorage.setItem("preferredLanguage", langCode);

    // Update HTML dir attribute for RTL
    const lang = languages.find((l) => l.code === langCode);
    document.documentElement.setAttribute("dir", lang?.rtl ? "rtl" : "ltr");
    document.documentElement.setAttribute("lang", langCode);

    setIsTranslating(false);
  }, []);

  return (
    <TranslateContext.Provider
      value={{
        currentLang,
        languages,
        changeLanguage,
        translateText,
        translateBatch,
        isTranslating,
        isEnglish: currentLang === "en",
      }}
    >
      {children}
    </TranslateContext.Provider>
  );
};

export const useTranslate = () => {
  const context = useContext(TranslateContext);
  if (!context) {
    throw new Error("useTranslate must be used within a TranslateProvider");
  }
  return context;
};

export default TranslateContext;